import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import CloseIcon from '@material-ui/icons/Close';
import { InfoCard, EmptyState } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { DeveloperPerformance, DeveloperAveragePerformance } from '../types';
import { SpiderChart } from './SpiderChart';

const CATEGORIES = [
  'Frontend Engineering',
  'Backend Systems',
  'Database & Data',
  'Infrastructure (DevOps)',
  'Mobile Development',
  'AI & Machine Learning',
  'Testing & QA',
  'Security & Auth',
  'Distributed Systems',
  'Documentation',
];

const SHORT_NAMES: Record<string, string> = {
  'Frontend Engineering': 'Frontend',
  'Backend Systems': 'Backend',
  'Database & Data': 'Database',
  'Infrastructure (DevOps)': 'DevOps',
  'Mobile Development': 'Mobile',
  'AI & Machine Learning': 'AI/ML',
  'Testing & QA': 'Testing',
  'Security & Auth': 'Security',
  'Distributed Systems': 'Distributed',
  'Documentation': 'Docs',
};

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  slotCard: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  loadedSlot: {
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  userIdLabel: {
    fontFamily: 'monospace',
    fontWeight: 700,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: 6,
    flexShrink: 0,
  },
  scoreCell: {
    fontWeight: 600,
  },
  betterCell: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  worseCell: {
    color: theme.palette.error.main,
  },
  chartSubtitle: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(3),
    justifyContent: 'center',
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface DeveloperSlot {
  userId: string;
  data: DeveloperPerformance | null;
  loading: boolean;
  error: string | null;
}

interface DeveloperComparisonProps {
  api: DevxpApi;
  initialUserIds: string[];
  onBack: () => void;
}

export const DeveloperComparison = ({ api, initialUserIds, onBack }: DeveloperComparisonProps) => {
  const classes = useStyles();

  const makeSlot = (userId = ''): DeveloperSlot => ({
    userId,
    data: null,
    loading: false,
    error: null,
  });

  const [slots, setSlots] = useState<[DeveloperSlot, DeveloperSlot]>([
    makeSlot(initialUserIds[0] ?? ''),
    makeSlot(initialUserIds[1] ?? ''),
  ]);
  const [inputValues, setInputValues] = useState<[string, string]>([
    initialUserIds[0] ?? '',
    initialUserIds[1] ?? '',
  ]);
  const [average, setAverage] = useState<DeveloperAveragePerformance | null>(null);

  const loadSlot = useCallback(
    async (index: 0 | 1, userId: string) => {
      if (!userId.trim()) return;
      setSlots(prev => {
        const updated = [...prev] as [DeveloperSlot, DeveloperSlot];
        updated[index] = { userId, data: null, loading: true, error: null };
        return updated;
      });
      try {
        const data = await api.getDeveloperPerformance(userId.trim());
        setSlots(prev => {
          const updated = [...prev] as [DeveloperSlot, DeveloperSlot];
          updated[index] = { userId, data, loading: false, error: null };
          return updated;
        });
      } catch (e: any) {
        setSlots(prev => {
          const updated = [...prev] as [DeveloperSlot, DeveloperSlot];
          updated[index] = { userId, data: null, loading: false, error: e.message ?? 'Failed to load' };
          return updated;
        });
      }
    },
    [api],
  );

  // Load average performance for baseline
  useEffect(() => {
    api.getDeveloperAveragePerformance().then(setAverage).catch(() => {});
  }, [api]);

  // Auto-load initially provided userIds
  useEffect(() => {
    if (initialUserIds[0]) loadSlot(0, initialUserIds[0]);
    if (initialUserIds[1]) loadSlot(1, initialUserIds[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearSlot = (index: 0 | 1) => {
    setSlots(prev => {
      const updated = [...prev] as [DeveloperSlot, DeveloperSlot];
      updated[index] = makeSlot('');
      return updated;
    });
    setInputValues(prev => {
      const updated = [...prev] as [string, string];
      updated[index] = '';
      return updated;
    });
  };

  // Build combined chart data
  const slot0Data = slots[0].data;
  const slot1Data = slots[1].data;

  const getCategoryValue = (perf: DeveloperPerformance | null, category: string) => {
    if (!perf) return null;
    const found = perf.categoryAverages.find(c => c.category === category);
    return found ? found.averageProficiency : 0;
  };

  const getAvgValue = (category: string) => {
    if (!average) return null;
    const found = average.categoryAverages.find(c => c.category === category);
    return found ? found.averageProficiency : 0;
  };

  const renderSlotHeader = (index: 0 | 1) => {
    const slot = slots[index];
    const color = index === 0 ? '#3f51b5' : '#e91e63';

    return (
      <Box className={`${classes.slotCard} ${slot.data ? classes.loadedSlot : ''}`}
        style={{ borderColor: slot.data ? color : undefined }}>
        {slot.loading ? (
          <CircularProgress size={24} />
        ) : slot.data ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }} width="100%">
            <span className={classes.legendDot} style={{ backgroundColor: color }} />
            <Typography className={classes.userIdLabel} style={{ flex: 1, textAlign: 'left' }}>
              {slot.userId}
            </Typography>
            <Chip label={`${slot.data.uniqueSkills.length} skills`} size="small" />
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => clearSlot(index)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <React.Fragment>
            <TextField
              size="small"
              label={`Developer ${index + 1}`}
              variant="outlined"
              value={inputValues[index]}
              onChange={e => {
                const val = e.target.value;
                setInputValues(prev => {
                  const updated = [...prev] as [string, string];
                  updated[index] = val;
                  return updated;
                });
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') loadSlot(index, inputValues[index]);
              }}
              style={{ width: '100%' }}
            />
            {slot.error && (
              <Typography variant="caption" color="error">{slot.error}</Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => loadSlot(index, inputValues[index])}
              disabled={!inputValues[index].trim()}
            >
              Load
            </Button>
          </React.Fragment>
        )}
      </Box>
    );
  };

  const bothLoaded = !!slot0Data && !!slot1Data;

  return (
    <Box>
      <Box className={classes.header}>
        <Tooltip title="Back to leaderboard">
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6">Developer Comparison</Typography>
      </Box>

      {/* Slot selectors */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6}>{renderSlotHeader(0)}</Grid>
        <Grid item xs={12} sm={6}>{renderSlotHeader(1)}</Grid>
      </Grid>

      {/* Overlaid spider chart */}
      {(slot0Data || slot1Data) && (
        <InfoCard title="Skill Radar Comparison">
          <SpiderChart
            primary={
              slot0Data
                ? { categoryAverages: slot0Data.categoryAverages }
                : { categoryAverages: [] }
            }
            compare={
              slot1Data
                ? { categoryAverages: slot1Data.categoryAverages }
                : undefined
            }
          />
          <Box className={classes.legend}>
            {slot0Data && (
              <Box className={classes.legendItem}>
                <span className={classes.legendDot} style={{ backgroundColor: '#3f51b5' }} />
                <Typography variant="caption">{slots[0].userId}</Typography>
              </Box>
            )}
            {slot1Data && (
              <Box className={classes.legendItem}>
                <span className={classes.legendDot} style={{ backgroundColor: '#e91e63' }} />
                <Typography variant="caption">{slots[1].userId}</Typography>
              </Box>
            )}
            {average && (
              <Box className={classes.legendItem}>
                <span className={classes.legendDot} style={{ backgroundColor: '#ccc' }} />
                <Typography variant="caption">Platform avg ({average.developerCount} devs)</Typography>
              </Box>
            )}
          </Box>
          <Typography className={classes.chartSubtitle}>
            Blue = {slots[0].userId || 'Developer 1'} · Pink = {slots[1].userId || 'Developer 2'}
          </Typography>
        </InfoCard>
      )}

      {/* Category breakdown table */}
      {bothLoaded && (
        <Box mt={2}>
          <InfoCard title="Category Breakdown">
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">{slots[0].userId}</TableCell>
                    <TableCell align="right">{slots[1].userId}</TableCell>
                    {average && <TableCell align="right">Platform Avg</TableCell>}
                    <TableCell align="center">Winner</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {CATEGORIES.map(cat => {
                    const v0 = getCategoryValue(slot0Data, cat) ?? 0;
                    const v1 = getCategoryValue(slot1Data, cat) ?? 0;
                    const avg = getAvgValue(cat);
                    const winner = v0 > v1 ? 0 : v1 > v0 ? 1 : -1;
                    return (
                      <TableRow key={cat}>
                        <TableCell>
                          <Typography variant="body2">{SHORT_NAMES[cat] ?? cat}</Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          className={winner === 0 ? classes.betterCell : classes.worseCell}
                        >
                          {v0.toFixed(2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          className={winner === 1 ? classes.betterCell : classes.worseCell}
                        >
                          {v1.toFixed(2)}
                        </TableCell>
                        {average && (
                          <TableCell align="right">
                            <Typography variant="caption" color="textSecondary">
                              {avg !== null ? avg.toFixed(2) : '–'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          {winner === -1 ? (
                            <Typography variant="caption" color="textSecondary">Tie</Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              style={{ fontFamily: 'monospace', fontWeight: 600 }}
                            >
                              {winner === 0 ? slots[0].userId : slots[1].userId}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        </Box>
      )}

      {!slot0Data && !slot1Data && (
        <EmptyState
          missing="data"
          title="No developers selected"
          description="Enter developer usernames above to start comparing."
        />
      )}
    </Box>
  );
};
